import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/apiClient';

/**
 * useListaHidratada — carga "hidratada" de listas grandes.
 *
 * Patrón: pinta la PRIMERA página al instante y completa el resto del dataset
 * en segundo plano, página por página, hasta tener TODO en memoria. Así la
 * pantalla abre rápido sin resignar completitud: los filtros y búsquedas en
 * memoria terminan operando sobre el 100 % de los registros.
 *
 * Todas las páginas son del mismo tamaño y el recorrido nunca vuelve atrás:
 * ninguna fila viaja dos veces. (Antes la primera página era chica y después
 * el fondo reempezaba desde la página 1 con páginas grandes, así que las
 * primeras filas se descargaban por duplicado.)
 *
 * Cuándo NO usar este hook: si la pantalla sólo necesita BUSCAR sobre la tabla
 * —no tenerla entera en memoria—, va `useBusquedaRemota`, que no descarga nada.
 * Este hook es para listas que se filtran y ordenan en el cliente.
 *
 * Reglas de oro (bugs históricos que este hook evita repetir):
 *  - La carga de fondo NUNCA se corta en silencio: si una página falla se
 *    reintenta con backoff y, si aun así falla, `error` queda seteado y
 *    `completo` en false para que la UI lo muestre.
 *  - Mientras `!completo`, la UI debe indicar que se sigue cargando
 *    (usar `hidratando` + `progreso`) para que el usuario sepa que un
 *    resultado de búsqueda puede estar incompleto TODAVÍA — nunca ocultarlo.
 *
 * @param {Object} opciones
 * @param {string} opciones.endpoint       Ruta relativa al apiClient, ej. 'equipos/'
 * @param {Object} [opciones.params]       Query params fijos (filtros del servidor)
 * @param {number} [opciones.primeraPagina=300]   Compatibilidad: si no hay pageSizeFondo, define el tamaño
 * @param {number} [opciones.pageSizeFondo=300]   Tamaño de TODAS las páginas del recorrido
 * @param {boolean} [opciones.habilitado=true]    Permite diferir la carga
 * @param {Function} [opciones.transformar]       map(row) aplicado a cada fila al llegar
 *
 * @returns {{
 *   rows: Array, hidratando: boolean, completo: boolean, cargandoInicial: boolean,
 *   progreso: {cargados: number, total: number|null}, error: any, refetch: Function,
 * }}
 */
export default function useListaHidratada({
  endpoint,
  params = undefined,
  primeraPagina = 300,
  pageSizeFondo = 300,
  habilitado = true,
  transformar = undefined,
}) {
  const [rows, setRows] = useState([]);
  const [cargandoInicial, setCargandoInicial] = useState(false);
  const [hidratando, setHidratando] = useState(false);
  const [completo, setCompleto] = useState(false);
  const [progreso, setProgreso] = useState({ cargados: 0, total: null });
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Serializamos los params para no re-disparar por identidad de objeto
  const paramsKey = JSON.stringify(params || {});
  const transformarRef = useRef(transformar);
  transformarRef.current = transformar;

  const refetch = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!habilitado || !endpoint) return undefined;

    const abort = new AbortController();
    let cancelado = false;
    const paramsFijos = JSON.parse(paramsKey);
    // `primeraPagina` y `pageSizeFondo` se mantienen por compatibilidad de API;
    // hoy el recorrido usa un tamaño único (ver el bucle de abajo).
    const tamPagina = pageSizeFondo || primeraPagina;

    const aplicarTransform = (lista) => {
      const fn = transformarRef.current;
      return typeof fn === 'function' ? lista.map(fn) : lista;
    };

    const getPagina = async (page, pageSize, reintentos = 2) => {
      try {
        const resp = await apiClient.get(endpoint, {
          params: { ...paramsFijos, page, page_size: pageSize },
          signal: abort.signal,
        });
        return resp.data;
      } catch (e) {
        if (cancelado || abort.signal.aborted || reintentos <= 0) throw e;
        // Backoff simple: la hidratación debe terminar, no morir en silencio
        await new Promise((r) => setTimeout(r, (3 - reintentos) * 1500 + 500));
        return getPagina(page, pageSize, reintentos - 1);
      }
    };

    const correr = async () => {
      setCargandoInicial(true);
      setHidratando(true);
      setCompleto(false);
      setError(null);
      try {
        // Un solo tamaño de página para todo el recorrido.
        //
        // Antes se pedía una primera página chica para pintar rápido y después
        // se ARRANCABA DE NUEVO desde la página 1 con páginas grandes, así que
        // las primeras filas se descargaban dos veces. En Reparaciones eso eran
        // 73 KB y una petición entera de más, en cada apertura. Con un tamaño
        // único la primera respuesta sigue pintando de inmediato y ninguna fila
        // viaja dos veces.
        const acumulado = new Map();
        let page = 1;
        for (;;) {
          const data = await getPagina(page, tamPagina);
          if (cancelado) return;

          // Respuesta no paginada (array plano): vino todo de una.
          if (Array.isArray(data)) {
            setRows(aplicarTransform(data));
            setProgreso({ cargados: data.length, total: data.length });
            setCargandoInicial(false);
            setCompleto(true);
            setHidratando(false);
            return;
          }

          const lista = data.results || [];
          for (const item of lista) acumulado.set(item.id ?? `${page}-${acumulado.size}`, item);
          const cargados = acumulado.size;
          setProgreso({ cargados, total: data.count ?? cargados });
          setRows(aplicarTransform(Array.from(acumulado.values())));
          // Ya hay algo en pantalla: el resto se completa de fondo.
          if (page === 1) setCargandoInicial(false);

          const hayMas = Boolean(data.next) && lista.length > 0;
          if (!hayMas) break;
          page += 1;
        }
        setCompleto(true);
        setHidratando(false);
      } catch (e) {
        if (cancelado || abort.signal.aborted) return;
        setError(e);
        setCargandoInicial(false);
        setHidratando(false);
        // OJO: completo queda en false → la UI debe avisar que la lista puede
        // estar incompleta y ofrecer reintentar (refetch).
      }
    };

    correr();
    return () => {
      cancelado = true;
      abort.abort();
    };
  }, [endpoint, paramsKey, primeraPagina, pageSizeFondo, habilitado, reloadTick]);

  return { rows, hidratando, completo, cargandoInicial, progreso, error, refetch };
}
